import { CameraOutlined, DeleteOutlined, LoadingOutlined } from '@ant-design/icons';
import { MessageCategoryConfig } from '@gooduncles/gu-app-schema';
import { Avatar, Button, Form, Input, Modal, Tooltip, Typography, Upload, message } from 'antd';
import type { RcFile } from 'antd/es/upload';
import { FC, useEffect, useState } from 'react';

import { AuditLogger } from 'src/utils/audit-logger';
import { getDownloadURL, updateChannelCategory, uploadFile } from 'src/utils/firebase/firebase-short-cut';

import styles from './ChannelCategoryConfig.module.scss';

const { Text } = Typography;
const auditLogger = AuditLogger.getInstance();

const ICON_STORAGE_PATH = 'channel-category-icon';

interface ChannelCategoryFormProps {
  open: boolean;
  onClose: () => void;
  editingCategory: { key: string; config: MessageCategoryConfig } | null;
  existingKeys: string[];
}

interface FormValues {
  categoryKey: string;
  label: string;
}

const ChannelCategoryForm: FC<ChannelCategoryFormProps> = ({ open, onClose, editingCategory, existingKeys }) => {
  const [form] = Form.useForm<FormValues>();
  const [loading, setLoading] = useState(false);
  const [iconUrl, setIconUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const isEdit = !!editingCategory;

  useEffect(() => {
    if (open) {
      if (editingCategory) {
        form.setFieldsValue({
          categoryKey: editingCategory.key,
          label: editingCategory.config.label,
        });
        setIconUrl(editingCategory.config.iconUrl || null);
      } else {
        form.resetFields();
        setIconUrl(null);
      }
    }
  }, [open, editingCategory, form]);

  const handleIconUpload = async (file: RcFile): Promise<false> => {
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      message.error('JPG, PNG, WebP 이미지만 업로드 가능합니다.');
      return false;
    }
    if (file.size > 5 * 1024 * 1024) {
      message.error('이미지 크기는 5MB 이하여야 합니다.');
      return false;
    }

    setUploading(true);
    try {
      const fileName = `${Date.now()}_${file.name}`;
      const storagePath = `${ICON_STORAGE_PATH}/${fileName}`;
      const result = await uploadFile(storagePath, file, {
        contentType: file.type,
        cacheControl: 'public,max-age=864000',
      });
      const url = await getDownloadURL(result.ref);
      setIconUrl(url);
    } catch (error) {
      console.error('Failed to upload icon image:', error);
      message.error('이미지 업로드에 실패했습니다.');
    } finally {
      setUploading(false);
    }

    return false;
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      if (!iconUrl) {
        message.error('아이콘 이미지를 업로드해주세요.');
        setLoading(false);
        return;
      }

      const categoryData: MessageCategoryConfig = {
        label: values.label,
        iconUrl,
      };

      await updateChannelCategory(values.categoryKey, categoryData);

      auditLogger.log({
        action: isEdit ? 'UPDATE' : 'CREATE',
        entity: 'config',
        entityId: 'channelCategory',
        summary: `채널 카테고리 ${isEdit ? '수정' : '생성'}: ${values.categoryKey} (${values.label})`,
      });

      message.success(`카테고리가 ${isEdit ? '수정' : '생성'}되었습니다.`);
      onClose();
    } catch (error) {
      console.error('Failed to save category:', error);
      message.error('카테고리 저장에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={null}
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      okText={isEdit ? '저장' : '생성'}
      cancelText='취소'
      confirmLoading={loading}
      width={420}
      destroyOnHidden
      maskClosable={false}
      keyboard={false}
      styles={{ body: { paddingTop: 0 } }}>
      <div className={styles.formHeader}>
        <Upload
          showUploadList={false}
          beforeUpload={handleIconUpload}
          accept='image/jpeg,image/png,image/webp'
          disabled={uploading}>
          <Tooltip title='클릭하여 아이콘 변경'>
            <div className={styles.avatarWrapper}>
              {uploading && <Avatar size={72} icon={<LoadingOutlined />} className={styles.avatar} />}
              {!uploading && iconUrl && <Avatar size={72} src={iconUrl} className={styles.avatar} />}
              {!uploading && !iconUrl && <Avatar size={72} icon={<CameraOutlined />} className={styles.avatarEmpty} />}
              <div className={styles.avatarOverlay}>
                <CameraOutlined style={{ fontSize: 16, color: '#fff' }} />
              </div>
            </div>
          </Tooltip>
        </Upload>
        {iconUrl && (
          <Button
            type='text'
            size='small'
            danger
            icon={<DeleteOutlined />}
            onClick={() => setIconUrl(null)}
            className={styles.removeIconBtn}>
            아이콘 삭제
          </Button>
        )}
        <Text strong style={{ fontSize: 16 }}>
          {isEdit ? '카테고리 수정' : '새 카테고리 생성'}
        </Text>
      </div>

      <Form form={form} layout='vertical' requiredMark='optional' className={styles.form}>
        <Form.Item
          name='categoryKey'
          label='카테고리 키'
          rules={[
            { required: true, message: '카테고리 키를 입력해주세요.' },
            { pattern: /^[a-z0-9-]+$/, message: '영문 소문자, 숫자, 하이픈만 사용 가능합니다.' },
            {
              validator: (_, value) => {
                if (!isEdit && value && existingKeys.includes(value)) {
                  return Promise.reject('이미 존재하는 카테고리 키입니다.');
                }
                return Promise.resolve();
              },
            },
          ]}>
          <Input placeholder='예: order, market-price' maxLength={30} disabled={isEdit} />
        </Form.Item>

        <Form.Item name='label' label='표시 이름' rules={[{ required: true, message: '표시 이름을 입력해주세요.' }]}>
          <Input placeholder='예: 주문, 시세' maxLength={20} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ChannelCategoryForm;
